import { useEffect, useRef, useState } from 'react';

const musicKey = 'nst-ranch-music-enabled';
const sfxKey = 'nst-ranch-sfx-enabled';

let sharedAudioContext: AudioContext | null = null;

function audioContext() {
  sharedAudioContext ??= new AudioContext();
  return sharedAudioContext;
}

function playTone(frequency: number, duration: number, volume: number) {
  if (localStorage.getItem(sfxKey) === 'false') return;
  try {
    const context = audioContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(volume, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + duration);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + duration);
  } catch { /* audio is optional when the wallet browser blocks autoplay */ }
}

export function playSuccessSound() {
  playTone(660, 0.18, 0.06);
  window.setTimeout(() => playTone(880, 0.22, 0.05), 90);
}

export function playErrorSound() {
  playTone(210, 0.25, 0.05);
}

export function GameAudio() {
  const [music, setMusic] = useState(localStorage.getItem(musicKey) === 'true');
  const [sfx, setSfx] = useState(localStorage.getItem(sfxKey) !== 'false');
  const musicNodes = useRef<{ oscillators: OscillatorNode[]; interval: number } | null>(null);

  useEffect(() => {
    if (!music) return;
    try {
      const context = audioContext();
      context.resume();
      const gain = context.createGain();
      gain.gain.value = 0.018;
      const filter = context.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 900;
      gain.connect(filter).connect(context.destination);
      const frequencies = [130.81, 196, 261.63];
      const oscillators = frequencies.map((frequency, index) => {
        const oscillator = context.createOscillator();
        oscillator.type = index === 0 ? 'sine' : 'triangle';
        oscillator.frequency.value = frequency;
        oscillator.connect(gain);
        oscillator.start();
        return oscillator;
      });
      let chord = 0;
      const progressions = [[130.81, 196, 261.63], [146.83, 220, 293.66], [110, 164.81, 220], [130.81, 196, 246.94]];
      const interval = window.setInterval(() => {
        chord = (chord + 1) % progressions.length;
        progressions[chord].forEach((frequency, index) => oscillators[index].frequency.linearRampToValueAtTime(frequency, context.currentTime + 1.5));
      }, 6000);
      musicNodes.current = { oscillators, interval };
      return () => {
        window.clearInterval(interval);
        gain.gain.linearRampToValueAtTime(0.0001, context.currentTime + 0.2);
        oscillators.forEach((oscillator) => oscillator.stop(context.currentTime + 0.25));
        musicNodes.current = null;
      };
    } catch {
      setMusic(false);
    }
  }, [music]);

  return <div className="audio-controls" aria-label="声音设置">
    <button aria-pressed={music} onClick={() => { const next = !music; localStorage.setItem(musicKey, String(next)); setMusic(next); }}>{music ? '🎵 音乐开' : '🔇 音乐关'}</button>
    <button aria-pressed={sfx} onClick={() => { const next = !sfx; localStorage.setItem(sfxKey, String(next)); setSfx(next); if (next) playTone(520, 0.16, 0.05); }}>{sfx ? '🔔 音效开' : '🔕 音效关'}</button>
  </div>;
}

const tutorialSteps = [
  { icon: '👛', title: '连接钱包', text: '签名登录不会消耗 Gas，同一个钱包可以恢复服务端账户和社交进度。' },
  { icon: '🌱', title: '链上种植', text: '购买种子、播种和收获都需要 MetaMask 确认，并记录在 Kava Testnet。' },
  { icon: '💎', title: '创世通行证', text: '10K 创世会员 NFT 共用一张主图，持有人享受链上收获加成，并领取项目方配置的 ERC-20 奖励。' },
  { icon: '🌆', title: '暮光集市', text: '加工收获物、完成委托、创建挂单或从其他牧场主那里购买商品。' },
  { icon: '🤝', title: '山谷社交', text: '进入山谷生活，参与实时聊天、公会、赛季榜和每日天气。' },
  { icon: '🐄', title: '动物小屋', text: '使用服务端金币领养、喂养动物，每天收取一次产物并获得金币和 XP。' }
];

export function TutorialOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [step, setStep] = useState(0);
  useEffect(() => { if (open) setStep(0); }, [open]);
  if (!open) return null;
  const current = tutorialSteps[step];
  function close() {
    localStorage.setItem('nst-ranch-tutorial-complete', 'true');
    onClose();
  }
  return <div className="tutorial-overlay" role="dialog" aria-modal="true" aria-label="牧野新手引导"><article><button className="tutorial-skip" onClick={close}>跳过</button><span className="tutorial-icon">{current.icon}</span><small>新手引导 {step + 1}/{tutorialSteps.length}</small><h3>{current.title}</h3><p>{current.text}</p><div className="tutorial-dots">{tutorialSteps.map((_, index) => <i key={index} className={index === step ? 'active' : ''} />)}</div><button className="primary large" onClick={() => step === tutorialSteps.length - 1 ? close() : setStep(step + 1)}>{step === tutorialSteps.length - 1 ? '开始经营牧场' : '下一步'}</button></article></div>;
}
